import { useEffect, useState } from "react";
import { matchPath, Navigate, useLocation } from "react-router-dom";
import PreLaunchPage from "@/pages/PreLaunchPages";
import { safeFetch } from "@/api/safeFetch";
import { useAuthToken } from "@/hooks/useAuthToken";

const PUBLIC_PRELAUNCH = [
  "/success",
  "/suscribirse",
  "/codigoRecuperacion",
  "/verificarCodigo",
  "/introduceNuevaContrasena",
  "/avisoLegal",
  "/politicaCookies",
  "/politicaPrivacidad",
  "/cookieConsentModal",
  "/paginaCookieRechazada",
];

const LOGGED_ONLY = [
  "/backoffice",
  "/InsertarSecciones",
  "/editarSecciones",
  "/borrarSecciones",
  "/insertarProfesiones",
  "/editarProfesion",
  "/borrarProfesiones",
  "/buscarProfesionales",
  "/editarProfesional",
  "/bajaProfesional",
  "/editarUsuarioAdmin",
  "/buscarUsuarioAdmin",
  "/gestionUsuarioAdmin",
  "/codigosAfiliado",
  "/afiliados",
  "/crearAfiliado",
  "/resumenAfiliado",
  "/editarAfiliado",
  "/bajaAfiliado",
  "/superAdmin",
  "/crearAdmin",
  "/facturasResumen",
  "/renovar/:profesional_id/:perfil_profesional_id",
  "/popuBajaDefinitiva",
  "/profesionales/:profesionalId/profesiones",
  "/editProfesional/:profesionalId/:profesionId/:perfilId",
  "/panelAfiliado",
  "/editaUsuario",
];

const ALWAYS_PUBLIC = ["/sign"];

const LaunchGate = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { roles, token } = useAuthToken();

  const [loading, setLoading] = useState(true);
  const [isLaunched, setIsLaunched] = useState(false);
  const [launchDate, setLaunchDate] = useState<Date | null>(null);

  const isLogged = !!token;

  // 🔥 FETCH LAUNCH CONFIG
  useEffect(() => {
    const fetchLaunchData = async () => {
      try {
        const res = await safeFetch(`/api/config/public?t=${Date.now()}`);
        const data = await res.json();

        if (data?.launchDate) {
          const parsedDate = new Date(data.launchDate);

          if (!isNaN(parsedDate.getTime())) {
            setLaunchDate(parsedDate);
          }
        }

        setIsLaunched(data?.isLaunched === true || data?.isLaunched === "true");
      } catch (err) {
        console.error("LaunchGate error:", err);

        // Si falla el servidor, dejamos pasar la web
        setIsLaunched(true);
      } finally {
        setLoading(false);
      }
    };

    fetchLaunchData();
  }, []);

  // 🔥 LOADING GLOBAL
  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#000",
          zIndex: 999999,
        }}
      />
    );
  }

  // 🔥 NORMALIZAR ROLES
  const normalizedRoles = Array.isArray(roles)
    ? roles.map((r) =>
        String(r)
          .replace(/^role_/i, "")
          .replace(/^role/i, "")
          .trim()
          .toLowerCase(),
      )
    : [];

  // 🔥 COMPROBAR SI LA FECHA YA HA PASADO
  const launchPassed =
    launchDate !== null && Date.now() >= launchDate.getTime();

  // 🔥 LANZAMIENTO ACTIVO
  const launched = isLaunched || launchPassed;

  // 🔥 RUTAS SIEMPRE PÚBLICAS
  const isAlwaysPublic = ALWAYS_PUBLIC.some((path) =>
    location.pathname.startsWith(path),
  );

  if (isAlwaysPublic) {
    return <>{children}</>;
  }

  // 🔥 PRELAUNCH GLOBAL
  if (!launched && !isLogged) {
    const allowed = PUBLIC_PRELAUNCH.some((path) =>
      location.pathname.startsWith(path),
    );

    if (!allowed) {
      return <PreLaunchPage launchDate={launchDate ?? new Date()} />;
    }
  }

  // 🔥 RUTAS SOLO PARA USUARIOS LOGUEADOS
  const isLoggedOnlyRoute = LOGGED_ONLY.some((path) => {
    // Renovar
    if (path === "/renovar/:profesional_id/:perfil_profesional_id") {
      return (
        matchPath(
          {
            path: "/renovar/:profesional_id/:perfil_profesional_id",
            end: true,
          },
          location.pathname,
        ) !== null
      );
    }

    // Ruta PRIVADA de profesiones de un profesional
    if (path === "/profesionales/:profesionalId/profesiones") {
      return (
        matchPath(
          { path: "/profesionales/:profesionalId/profesiones", end: true },
          location.pathname,
        ) !== null
      );
    }

    // Edición de profesional
    if (path === "/editProfesional/:profesionalId/:profesionId/:perfilId") {
      return (
        matchPath(
          {
            path: "/editProfesional/:profesionalId/:profesionId/:perfilId",
            end: true,
          },
          location.pathname,
        ) !== null
      );
    }

    return location.pathname.startsWith(path);
  });

  if (isLoggedOnlyRoute && !isLogged) {
    return <Navigate to="/sign" replace />;
  }

  // 🔥 BACKOFFICE SOLO ADMIN
  if (
    location.pathname.startsWith("/backoffice") &&
    !(
      normalizedRoles.includes("admin") ||
      normalizedRoles.includes("superadmin")
    )
  ) {
    return <Navigate to="/" replace />;
  }

  // 🔥 PANEL AFILIADO
  if (
    location.pathname.startsWith("/panelAfiliado") &&
    !(
      normalizedRoles.includes("afiliado") ||
      normalizedRoles.includes("profesional") ||
      normalizedRoles.includes("admin") ||
      normalizedRoles.includes("superadmin")
    )
  ) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default LaunchGate;
